import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { fetchDialog } from "@/lib/content-api";
import { useTrainingStore } from "@/features/training";
import { useAuthStore } from "@/features/auth";
import {
  DialogHeader,
  DialogLineList,
  AudioPlayer,
  RolePlayModal,
  NotesSection,
  CreditGate,
  useDialogStore,
} from "@/features/dialog";

declare global {
  interface Window {
    GlossikaDictionary?: {
      init: (opts: { email: string; lang: string }) => void;
      lookup: (word: string, lang: string) => void;
      close: () => void;
    };
  }
}

const PREVIEW_LINES = 5;

function parseLineHash(hash: string): number | null {
  const match = /^#line=(\d+)$/.exec(hash);
  if (!match?.[1]) return null;
  return parseInt(match[1], 10);
}

function slugToTitle(slug: string): string {
  return slug
    .replace(/-[a-f0-9]{6,}$/i, "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function DialogPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const dialog = useDialogStore((s) => s.dialog);
  const loading = useDialogStore((s) => s.loading);
  const error = useDialogStore((s) => s.error);
  const setDialog = useDialogStore((s) => s.setDialog);
  const setError = useDialogStore((s) => s.setError);
  const setLoading = useDialogStore((s) => s.setLoading);
  const setRolePlay = useDialogStore((s) => s.setRolePlay);
  const reset = useDialogStore((s) => s.reset);
  const [unlocked, setUnlocked] = useState(false);

  const user = useAuthStore((s) => s.user);
  const addToTraining = useTrainingStore((s) => s.addDialog);
  const isAlreadyUnlocked = useTrainingStore((s) => s.isUnlocked);
  const loadTraining = useTrainingStore((s) => s.loadTraining);
  const trainingLoaded = useTrainingStore((s) => s.loaded);

  const scrollToLine = useMemo(
    () => parseLineHash(location.hash),
    [location.hash],
  );

  useEffect(() => {
    if (!trainingLoaded) loadTraining();
  }, [trainingLoaded, loadTraining]);

  useEffect(() => {
    if (slug && trainingLoaded && isAlreadyUnlocked(slug)) {
      setUnlocked(true);
    }
  }, [slug, trainingLoaded, isAlreadyUnlocked]);

  useEffect(() => {
    if (!slug) {
      setError("No dialog slug provided");
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchDialog(slug)
      .then((d) => {
        if (!cancelled) setDialog(d);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load dialog");
      });

    return () => {
      cancelled = true;
      reset();
      document.getElementById("gt-container")?.remove();
      window.GlossikaDictionary?.close();
    };
  }, [slug, setDialog, setError, setLoading, reset]);

  // Load and initialize Glossika Dictionary widget
  useEffect(() => {
    if (!dialog) return;
    const email = user?.email ?? "anonymous";
    const rawLang = dialog.language_code.split("_")[0]!.toLowerCase();
    const lang = rawLang === "nor" ? "nob" : rawLang;

    if (window.GlossikaDictionary) {
      window.GlossikaDictionary.init({ email, lang });
      return;
    }

    const existing = document.getElementById("glossika-dict-script");
    if (existing) return;

    const script = document.createElement("script");
    script.id = "glossika-dict-script";
    script.src = "/dict-widget.js?v=8";
    script.onload = () => {
      window.GlossikaDictionary?.init({ email, lang });
    };
    document.head.appendChild(script);
  }, [dialog, user?.email]);

  function handleUnlock() {
    setUnlocked(true);
    if (dialog && slug) {
      const title = dialog.title_english || slugToTitle(dialog.slug || slug);
      addToTraining({
        slug: dialog.slug || slug,
        title,
        languageCode: dialog.language_code,
        level: dialog.level,
        speakers: dialog.speakers.map((s) => s.name).join(" & "),
      });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-glossika-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!dialog) return null;

  const isPreview = !unlocked && dialog.lines.length > PREVIEW_LINES;
  const displayDialog = isPreview
    ? { ...dialog, lines: dialog.lines.slice(0, PREVIEW_LINES) }
    : dialog;

  return (
    <>
      <DialogHeader dialog={dialog} />

      <AudioPlayer
        dialog={displayDialog}
        onOpenRoleplay={() => setRolePlay({ isOpen: true })}
      />

      <DialogLineList dialog={displayDialog} scrollToLine={scrollToLine} />

      {isPreview ? (
        <CreditGate
          dialogId={dialog.id}
          dialogTitle={dialog.title_english || slugToTitle(dialog.slug || slug || "")}
          onUnlock={handleUnlock}
        />
      ) : (
        <>
          <NotesSection dialog={dialog} />

          <div className="text-center mt-6">
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-indigo-500/25"
            >
              Browse Dialogs
            </Link>
          </div>
        </>
      )}

      <RolePlayModal dialog={displayDialog} unlocked={unlocked} onUnlock={handleUnlock} />
    </>
  );
}
